import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatINR, formatDate, formatDateTime } from "../lib/api";
import { Plus, Trash2, CheckCircle2, Clock, RotateCcw, IndianRupee, Wallet2, Search, Landmark, Link2, X, MessageCircle, Users } from "lucide-react";
import { usePagination } from "../hooks/usePagination";
import { Pagination } from "../components/Pagination";
import { UpiPayModal } from "../components/UpiPayModal";
import { PayeeManager } from "../components/PayeeManager";
import { buildUpiLink } from "../lib/upi";
import { whatsappLink } from "../lib/whatsapp";
import { toast } from "sonner";

const REF_LABELS = { lr: "LR", expense: "Expense", broker: "Broker", driver: "Driver", sub: "Sub-transporter", personal: "Personal", vehicle: "Vehicle", other: "—" };

export default function Payments() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ pending_count: 0, pending_total: 0, paid_month_count: 0, paid_month_total: 0 });
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null); // prefill object or null
  const [recon, setRecon] = useState(null); // { payment, candidates }
  const [payees, setPayees] = useState([]);
  const [reconcilingAll, setReconcilingAll] = useState(false);
  const [showPayeeMgr, setShowPayeeMgr] = useState(false);
  const [company, setCompany] = useState(null);
  const paged = usePagination(items, 25);

  async function load() {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (q) params.q = q;
    const [{ data }, s, pe] = await Promise.all([
      api.get("/payments", { params }),
      api.get("/payments/summary").then((r) => r.data).catch(() => null),
      api.get("/payees").then((r) => r.data).catch(() => []),
    ]);
    setItems(data);
    if (s) setSummary(s);
    setPayees(pe || []);
  }
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [statusFilter, q]); // eslint-disable-line
  useEffect(() => { api.get("/company").then((r) => setCompany(r.data)).catch(() => {}); }, []);

  function sendWhatsApp(p) {
    if (!p.phone) { toast.error("Add a phone number to this payment to send a WhatsApp receipt"); return; }
    const who = p.payee_name ? `${p.payee_name}, ` : "";
    const forNote = p.note ? ` for ${p.note}` : (p.category ? ` for ${p.category}` : "");
    const sign = company?.name ? `\n— ${company.name}` : "";
    const msg = `Hi ${who}we've sent you *${formatINR(p.amount)}* via UPI${forNote}.${sign}`;
    window.open(whatsappLink(p.phone, msg), "_blank");
  }

  async function reconcileAll() {
    setReconcilingAll(true);
    try {
      const { data } = await api.post("/payments/reconcile-all", {});
      if (data.linked > 0) toast.success(`Matched ${data.linked} payment(s) to your bank statement`);
      else toast.info(`No unique matches found (${data.ambiguous} need manual picking, ${data.none} not in statement yet)`);
      load();
    } catch { toast.error("Could not reconcile"); }
    finally { setReconcilingAll(false); }
  }
  function payFavourite(pe) {
    setModal({ payee_name: pe.name, upi_id: pe.upi_id || "", phone: pe.phone || "", amount: "", note: "", category: pe.category || "", ref_type: "other" });
  }

  async function markPaid(p) {
    try {
      const { data } = await api.post(`/payments/${p.id}/mark-paid`, {});
      if (data?.auto_reconciled) toast.success("Marked paid & auto-matched to your bank statement");
      else toast.success("Marked as paid");
      load();
    } catch { toast.error("Could not update"); }
  }
  async function reconcile(p) {
    try {
      const { data } = await api.post(`/payments/${p.id}/auto-tally`, {});
      if (data.matched) { toast.success("Auto-matched to your bank statement"); load(); return; }
      if ((data.candidates || []).length === 0) { toast.info("No matching untallied bank debit found yet"); return; }
      setRecon({ payment: p, candidates: data.candidates });
    } catch { toast.error("Could not reconcile"); }
  }
  async function linkTxn(txnId) {
    try {
      await api.post(`/payments/${recon.payment.id}/link-txn`, { txn_id: txnId });
      toast.success("Linked to bank statement"); setRecon(null); load();
    } catch { toast.error("Could not link"); }
  }
  async function unlink(p) {
    try { await api.post(`/payments/${p.id}/unlink`, {}); toast.success("Unlinked from bank row"); load(); }
    catch { toast.error("Could not unlink"); }
  }
  async function markPending(p) {
    try { await api.post(`/payments/${p.id}/mark-pending`, {}); toast.success("Moved back to pending"); load(); }
    catch { toast.error("Could not update"); }
  }
  async function remove(p) {
    if (!confirm("Delete this payment record?")) return;
    try { await api.delete(`/payments/${p.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  }
  function payAgain(p) {
    setModal({ payee_name: p.payee_name, upi_id: p.upi_id, amount: p.amount, note: p.note, category: p.category, ref_type: p.ref_type, ref_id: p.ref_id, ref_number: p.ref_number });
  }

  const refLink = (p) => {
    if (p.ref_type === "lr" && p.ref_id) return () => navigate(`/lr/${p.ref_id}`);
    if (p.ref_type === "expense") return () => navigate("/expenses");
    if (p.ref_type === "personal") return () => navigate("/personal-accounts");
    return null;
  };

  return (
    <div data-testid="payments-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#68665E] font-semibold">Cash Flow</div>
          <h1 className="font-headings text-4xl font-bold text-[#1A1A18] mt-1">UPI Payments</h1>
          <p className="text-sm text-[#68665E] mt-2">Pay drivers, brokers &amp; vendors by UPI and keep a clean ledger.</p>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => setShowPayeeMgr(true)} data-testid="manage-payees-button" title="Add, edit or remove favourite payees">
            <Users className="w-4 h-4" /> Payees
          </button>
          <button className="btn-secondary" onClick={reconcileAll} disabled={reconcilingAll} data-testid="reconcile-all-button" title="Match every paid payment to its bank debit row">
            <Landmark className="w-4 h-4" /> {reconcilingAll ? "Matching…" : "Match all to bank"}
          </button>
          <button className="btn-primary" onClick={() => setModal({ ref_type: "other" })} data-testid="new-payment-button">
            <Plus className="w-4 h-4" /> New Payment
          </button>
        </div>
      </div>

      {payees.length > 0 && (
        <div className="mb-6" data-testid="favourite-payees">
          <div className="text-[10px] uppercase tracking-widest text-[#68665E] font-semibold mb-2">Favourite payees — tap to pay</div>
          <div className="flex flex-wrap gap-2">
            {payees.slice(0, 12).map((pe) => (
              <button key={pe.id} onClick={() => payFavourite(pe)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[hsl(var(--border))] text-sm hover:border-[#C84B31] hover:text-[#C84B31] transition-colors" data-testid={`favourite-payee-${pe.id}`}>
                <IndianRupee className="w-3.5 h-3.5" /> {pe.name || pe.upi_id || pe.phone}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="card-surface p-5" data-testid="pay-summary-pending">
          <div className="flex items-center gap-2 text-[#8A2526]"><Clock className="w-4 h-4" /><span className="text-xs uppercase tracking-widest font-semibold">Pending</span></div>
          <div className="font-headings text-3xl font-bold mt-2">{formatINR(summary.pending_total)}</div>
          <div className="text-xs text-[#68665E] mt-1">{summary.pending_count} payment(s) awaiting</div>
        </div>
        <div className="card-surface p-5" data-testid="pay-summary-paid-month">
          <div className="flex items-center gap-2 text-[#1f8a4c]"><CheckCircle2 className="w-4 h-4" /><span className="text-xs uppercase tracking-widest font-semibold">Paid this month</span></div>
          <div className="font-headings text-3xl font-bold mt-2">{formatINR(summary.paid_month_total)}</div>
          <div className="text-xs text-[#68665E] mt-1">{summary.paid_month_count} payment(s) this month</div>
        </div>
        <div className="card-surface p-5" data-testid="pay-summary-total">
          <div className="flex items-center gap-2 text-[#C84B31]"><Wallet2 className="w-4 h-4" /><span className="text-xs uppercase tracking-widest font-semibold">All payments</span></div>
          <div className="font-headings text-3xl font-bold mt-2">{items.length}</div>
          <div className="text-xs text-[#68665E] mt-1">shown with current filters</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#68665E]" />
          <input className="input-field pl-9 w-72" placeholder="Search payee, UPI, LR, note..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="payment-search" />
        </div>
        <select className="input-field w-40" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="payment-status-filter">
          <option value="">All status</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      <Pagination {...paged} testId="payments-pagination" />
      <div className="table-wrap">
        <table className="w-full lrs-table">
          <thead>
            <tr>
              <th className="table-th nowrap">Date</th>
              <th className="table-th">Payee</th>
              <th className="table-th">UPI ID</th>
              <th className="table-th nowrap text-right">Amount</th>
              <th className="table-th">For</th>
              <th className="table-th nowrap">Status</th>
              <th className="table-th nowrap text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td className="table-td text-[#68665E] text-center" colSpan={7}>No payments yet. Tap “New Payment” or use “Pay via UPI” from an LR, expense or master record.</td></tr>
            ) : paged.pageItems.map((p) => {
              const go = refLink(p);
              return (
                <tr key={p.id} className="table-row" data-testid={`payment-row-${p.id}`}>
                  <td className="table-td nowrap">{p.status === "paid" && p.paid_date ? formatDate(p.paid_date) : formatDateTime(p.created_at)}</td>
                  <td className="table-td min-w-[140px] max-w-[220px]">
                    <div className="font-medium truncate" title={p.payee_name}>{p.payee_name || "—"}</div>
                    {p.category ? <div className="text-xs text-[hsl(var(--text-muted))] truncate">{p.category}</div> : (p.note ? <div className="text-xs text-[hsl(var(--text-muted))] truncate" title={p.note}>{p.note}</div> : null)}
                  </td>
                  <td className="table-td font-mono text-xs break-all max-w-[180px]">{p.upi_id || (p.phone ? `Ph: ${p.phone}` : "—")}</td>
                  <td className="table-td nowrap data-num font-medium text-right">{formatINR(p.amount)}</td>
                  <td className="table-td nowrap">
                    {p.ref_number ? (
                      go ? <button className="text-[#C84B31] hover:underline" onClick={go} data-testid={`payment-ref-${p.id}`}>{p.ref_number}</button>
                        : <span>{p.ref_number}</span>
                    ) : <span className="text-[#A09E96]">{REF_LABELS[p.ref_type] || "—"}</span>}
                  </td>
                  <td className="table-td nowrap">
                    {p.status === "paid"
                      ? <span className="badge-success">paid</span>
                      : <span className="badge-pending">pending</span>}
                    {p.reconciled
                      ? <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-[#1f8a4c]" title={p.reconciled_label} data-testid={`payment-reconciled-${p.id}`}><Link2 className="w-3 h-3" /> bank matched</div>
                      : null}
                  </td>
                  <td className="table-td nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      <a href={buildUpiLink(p)} className="btn-ghost" title="Open in UPI app" data-testid={`payment-pay-${p.id}`}><IndianRupee className="w-4 h-4" /></a>
                      <button className="btn-ghost text-[#1f8a4c]" title="Send WhatsApp receipt" onClick={() => sendWhatsApp(p)} data-testid={`payment-whatsapp-${p.id}`}><MessageCircle className="w-4 h-4" /></button>
                      {p.status === "pending"
                        ? <button className="btn-ghost text-[#1f8a4c]" title="Mark as paid" onClick={() => markPaid(p)} data-testid={`payment-markpaid-${p.id}`}><CheckCircle2 className="w-4 h-4" /></button>
                        : <button className="btn-ghost" title="Move back to pending" onClick={() => markPending(p)} data-testid={`payment-markpending-${p.id}`}><RotateCcw className="w-4 h-4" /></button>}
                      {p.reconciled
                        ? <button className="btn-ghost text-[#8A2526]" title="Unlink from bank row" onClick={() => unlink(p)} data-testid={`payment-unlink-${p.id}`}><Link2 className="w-4 h-4" /></button>
                        : <button className="btn-ghost" title="Match to bank statement" onClick={() => reconcile(p)} data-testid={`payment-reconcile-${p.id}`}><Landmark className="w-4 h-4" /></button>}
                      <button className="btn-ghost" title="Pay again" onClick={() => payAgain(p)} data-testid={`payment-again-${p.id}`}><Plus className="w-4 h-4" /></button>
                      <button className="btn-ghost text-[#8A2526]" title="Delete" onClick={() => remove(p)} data-testid={`payment-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <UpiPayModal open={!!modal} prefill={modal || {}} onClose={() => setModal(null)} onSaved={() => load()} />
      <PayeeManager open={showPayeeMgr} onClose={() => setShowPayeeMgr(false)} onChanged={() => load()} />

      {recon && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-start justify-center overflow-y-auto py-10" data-testid="reconcile-modal" onClick={() => setRecon(null)}>
          <div className="bg-[hsl(var(--surface))] text-[hsl(var(--text-main))] w-full max-w-lg mx-4 rounded-sm border border-[hsl(var(--border))]" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
              <div>
                <h2 className="font-headings text-lg font-bold">Match to bank statement</h2>
                <p className="text-xs text-[hsl(var(--text-muted))] mt-0.5">Pick the debit row for {formatINR(recon.payment.amount)} to {recon.payment.payee_name || "this payee"}.</p>
              </div>
              <button className="btn-ghost" onClick={() => setRecon(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="divide-y divide-[hsl(var(--border))] max-h-[60vh] overflow-y-auto">
              {recon.candidates.map((c) => (
                <button key={c.id} onClick={() => linkTxn(c.id)} className="w-full text-left px-6 py-3 hover:bg-[hsl(var(--text-main)/0.04)] flex items-center justify-between gap-3" data-testid={`reconcile-cand-${c.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.description || "Bank debit"}</div>
                    <div className="text-xs text-[hsl(var(--text-muted))]">{formatDate(c.date)} · {c.account_label}{typeof c.gap_days === "number" ? ` · ${c.gap_days}d away` : ""}</div>
                  </div>
                  <span className="text-sm font-semibold text-[#8A2526] shrink-0">{formatINR(c.debit)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
